import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.spring
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.focus.FocusDirection
import androidx.compose.ui.focus.FocusManager
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.semantics.selected
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.platform.SoftwareKeyboardController
import androidx.compose.ui.text.font.FontWeight.Companion.SemiBold
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import models.LimitedTimeGameModes
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import banko.composeapp.generated.resources.*

@Composable
fun BankoStartScreenContent(
    modifier: Modifier = Modifier,
    isMobile: Boolean,
    isPhoneLandscape: Boolean = false,
    onOpenDice: () -> Unit,
    userName: String,
    gameCode: String,
    isOfflineMode: Boolean,
    isLoading: Boolean,
    focusManager: FocusManager,
    keyboardController: SoftwareKeyboardController?,
    coroutineScope: CoroutineScope,
    uiState: BankoStartScreenUiState
) {
    if (isPhoneLandscape) {
        // Side-by-side on short landscape windows: branding on the left, the entry form on the right.
        // Splitting the content across the wide axis keeps each side short enough to fit the limited
        // height, and — crucially — the branding lives in its own non-scrolling pane, so focusing a
        // field (which scrolls the form into view) can never push the title off the top of the screen.
        Row(
            modifier = modifier,
            horizontalArrangement = Arrangement.spacedBy(24.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(
                modifier = Modifier.weight(1f),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                Icon(
                    imageVector = BankoIcons.Casino,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(44.dp).bounceOn(Unit),
                )
                Text(
                    fontSize = 40.sp,
                    text = stringResource(Res.string.banko_title),
                    textAlign = TextAlign.Center,
                    color = MaterialTheme.colorScheme.primary,
                    fontFamily = theme.bankoDisplayFontFamily(),
                )
                Text(
                    text = stringResource(Res.string.app_tagline),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.7f),
                    textAlign = TextAlign.Center,
                )
            }
            Column(
                // No fillMaxHeight: the pane wraps its content (so the Row centers it vertically when
                // it fits) and only becomes scrollable once a very short window can't.
                modifier = Modifier
                    .weight(1f)
                    .verticalScroll(rememberScrollState()),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                StartGameForm(
                    fieldModifier = Modifier.fillMaxWidth().widthIn(max = 420.dp),
                    primaryButtonHeight = 48.dp,
                    userName = userName,
                    gameCode = gameCode,
                    isOfflineMode = isOfflineMode,
                    isLoading = isLoading,
                    focusManager = focusManager,
                    keyboardController = keyboardController,
                    coroutineScope = coroutineScope,
                    uiState = uiState,
                    onOpenDice = onOpenDice,
                )
            }
        }
        return
    }

    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(24.dp)
    ) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(4.dp),
        ) {
            Icon(
                imageVector = BankoIcons.Casino,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary,
                modifier = Modifier.size(if (isMobile) 48.dp else 60.dp).bounceOn(Unit),
            )
            Text(
                fontSize = if (isMobile) 48.sp else 60.sp,
                text = stringResource(Res.string.banko_title),
                textAlign = TextAlign.Center,
                color = MaterialTheme.colorScheme.primary,
                fontFamily = theme.bankoDisplayFontFamily(),
                modifier = Modifier.fillMaxWidth()
            )
            Text(
                text = stringResource(Res.string.app_tagline),
                style = MaterialTheme.typography.bodyLarge,
                color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.7f),
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth(),
            )
        }
        StartGameForm(
            fieldModifier = Modifier.fillMaxWidth(),
            primaryButtonHeight = 56.dp,
            userName = userName,
            gameCode = gameCode,
            isOfflineMode = isOfflineMode,
            isLoading = isLoading,
            focusManager = focusManager,
            keyboardController = keyboardController,
            coroutineScope = coroutineScope,
            uiState = uiState,
            onOpenDice = onOpenDice,
        )
    }
}

/**
 * The core "get into a game" form: name field, optional game-code field, the create/join button, and
 * the find-public-game shortcut. Shared by the portrait/desktop and landscape layouts so the entry
 * flow stays identical; only the surrounding chrome (title size, width caps) differs.
 */
@Composable
private fun StartGameForm(
    fieldModifier: Modifier,
    primaryButtonHeight: Dp,
    userName: String,
    gameCode: String,
    isOfflineMode: Boolean,
    isLoading: Boolean,
    focusManager: FocusManager,
    keyboardController: SoftwareKeyboardController?,
    coroutineScope: CoroutineScope,
    uiState: BankoStartScreenUiState,
    onOpenDice: () -> Unit,
) {
    val isOfflineToggleLocked by uiState.isOfflineToggleLockedFlow.collectAsState()
    val selectedDiceIds = selectedDiceIds(uiState)
    val practiceOfferVisible by uiState.practiceOfferVisibleFlow.collectAsState()
    if (practiceOfferVisible) {
        PracticeOfferCard(
            enabled = !isLoading,
            onQuickPlay = uiState.onQuickPlay,
            onDismiss = uiState.onDismissPracticeOffer,
            modifier = fieldModifier,
        )
    }
    ModeChooser(
        isSingleDevice = isOfflineMode,
        onlineLocked = isOfflineToggleLocked,
        onSelectMode = uiState.onToggleOfflineMode,
        modifier = fieldModifier,
    )
    if (isOfflineToggleLocked) {
        Text(
            text = stringResource(Res.string.settings_offline_locked),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.error,
            textAlign = TextAlign.Center,
            modifier = fieldModifier,
        )
    }
    BankoTextField(
        modifier = fieldModifier,
        value = userName,
        onValueChange = uiState.onUpdateUserName,
        keyboardOptions = KeyboardOptions(imeAction = if (isOfflineMode) ImeAction.Done else ImeAction.Next),
        keyboardActions = KeyboardActions(
            onNext = { focusManager.moveFocus(FocusDirection.Down) },
            onDone = {
                if (isOfflineMode && userName.isNotEmpty()) {
                    coroutineScope.launch {
                        keyboardController?.hide()
                        delay(500)
                        uiState.onCreateGame()
                    }
                }
            }
        ),
        placeholder = stringResource(Res.string.enter_name),
    )
    if (!isOfflineMode) {
        BankoTextField(
            modifier = fieldModifier,
            value = gameCode,
            onValueChange = uiState.onUpdateGameCode,
            keyboardOptions = KeyboardOptions(
                capitalization = KeyboardCapitalization.Characters,
                imeAction = ImeAction.Done,
            ),
            keyboardActions = KeyboardActions(onDone = {
                coroutineScope.launch {
                    keyboardController?.hide()
                    delay(500)
                    // Same rule as the button: a partial code neither joins nor creates.
                    if (userName.isNotEmpty()) {
                        if (gameCode.isEmpty()) {
                            uiState.onCreateGame()
                        } else if (gameCode.length == 5) {
                            uiState.onJoinGame()
                        }
                    }
                }
            }),
            placeholder = stringResource(Res.string.game_code_placeholder),
        )
    }
    val createButtonText = if (isOfflineMode) {
        stringResource(Res.string.start_single_device_game)
    } else if (gameCode.isEmpty()) {
        stringResource(Res.string.create_game)
    } else {
        stringResource(Res.string.join_game)
    }
    GlossyPrimaryButton(
        text = createButtonText,
        loading = isLoading,
        enabled = if (isOfflineMode) {
            userName.isNotEmpty()
        } else {
            (gameCode.length == 5 || gameCode.isEmpty()) && userName.isNotEmpty()
        },
        onClick = {
            coroutineScope.launch {
                keyboardController?.hide()
                delay(500)
                if (isOfflineMode) {
                    uiState.onCreateGame()
                } else {
                    if (gameCode.isEmpty()) {
                        uiState.onCreateGame()
                    } else {
                        uiState.onJoinGame()
                    }
                }
            }
        },
        modifier = fieldModifier.height(primaryButtonHeight),
    )
    // An unspent Plus Pass applies itself to the next created game — remind the host it's waiting.
    val plusPassPending by PlusPass.pending.collectAsState()
    if (plusPassPending) {
        Text(
            text = stringResource(Res.string.plus_pass_pending_hint),
            style = MaterialTheme.typography.labelLarge,
            color = MaterialTheme.colorScheme.primary,
            textAlign = TextAlign.Center,
            modifier = fieldModifier,
        )
    }
    // Public matchmaking is a returning-player option. Hiding the cold-start browser from brand-new
    // players keeps the first screen focused on the guaranteed-value paths: practice or host/join.
    if (!isOfflineMode && !practiceOfferVisible) {
        GlossySecondaryButton(
            text = stringResource(Res.string.find_public_game),
            onClick = uiState.onShowPublicGames,
            enabled = !isLoading,
            modifier = fieldModifier.height(primaryButtonHeight),
        )
    }
    // Lifetime stats live one tap from home: wins, streaks, banking style, dice luck and rivals.
    YourStatsRow(modifier = fieldModifier)
    CurrentDiceEntry(
        selectedSkinId = selectedDiceIds.first,
        selectedHatId = selectedDiceIds.second,
        onClick = onOpenDice,
        modifier = fieldModifier,
    )
}

/**
 * One-time offer for brand-new players: a single tap drops them into a started local game against
 * three bots so they learn the roll/bank rhythm by playing, before ever seeing a lobby. Shown only
 * until they enter (or decline) their first game — returning players never see it.
 */
@Composable
private fun PracticeOfferCard(
    enabled: Boolean,
    onQuickPlay: () -> Unit,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Surface(
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colorScheme.surfaceContainerHigh,
        shadowElevation = 2.dp,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        modifier = modifier,
    ) {
        Column(modifier = Modifier.padding(horizontal = 16.dp, vertical = 14.dp)) {
            Text(
                text = stringResource(Res.string.practice_offer_title),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = SemiBold,
                color = MaterialTheme.colorScheme.onSurface,
            )
            Spacer(modifier = Modifier.height(4.dp))
            Text(
                text = stringResource(Res.string.practice_offer_body),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Spacer(modifier = Modifier.height(10.dp))
            GlossyPrimaryButton(
                text = stringResource(Res.string.practice_offer_play),
                onClick = onQuickPlay,
                enabled = enabled,
                fontSize = 16.sp,
                modifier = Modifier.fillMaxWidth().height(46.dp),
            )
            TextButton(
                onClick = onDismiss,
                enabled = enabled,
                modifier = Modifier.align(Alignment.CenterHorizontally),
            ) {
                Text(text = stringResource(Res.string.practice_offer_dismiss))
            }
        }
    }
}

/**
 * The top-of-form mode chooser: online (networked — join by code or Find a Game) vs single device
 * (one shared phone). Surfacing both as a first-class choice, instead of a hidden settings toggle, so
 * players discover single-device play. The online segment is disabled while there's no network, since
 * the app forces single-device play then.
 */
@Composable
private fun ModeChooser(
    isSingleDevice: Boolean,
    onlineLocked: Boolean,
    onSelectMode: (singleDevice: Boolean) -> Unit,
    modifier: Modifier = Modifier,
) {
    // The gold selection pill slides between the segments rather than snapping, like a native
    // segmented control.
    val pillFraction by animateFloatAsState(
        targetValue = if (isSingleDevice) 1f else 0f,
        animationSpec = spring(dampingRatio = 0.85f, stiffness = Spring.StiffnessMediumLow),
        label = "modePill",
    )
    Surface(
        shape = RoundedCornerShape(percent = 50),
        color = MaterialTheme.colorScheme.surfaceContainerHigh,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        shadowElevation = 2.dp,
        modifier = modifier,
    ) {
        BoxWithConstraints(modifier = Modifier.padding(4.dp)) {
            val segmentWidth = (maxWidth - 4.dp) / 2
            Box(
                modifier = Modifier
                    .matchParentSize()
                    .wrapContentWidth(Alignment.Start)
                    .offset(x = (segmentWidth + 4.dp) * pillFraction)
                    .width(segmentWidth)
                    .background(MaterialTheme.colorScheme.primary, RoundedCornerShape(percent = 50)),
            )
            Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                ModeSegment(
                    text = stringResource(Res.string.mode_online),
                    selected = !isSingleDevice,
                    enabled = !onlineLocked,
                    onClick = { onSelectMode(false) },
                )
                ModeSegment(
                    text = stringResource(Res.string.settings_offline_mode),
                    selected = isSingleDevice,
                    enabled = true,
                    onClick = { onSelectMode(true) },
                )
            }
        }
    }
}

@Composable
private fun RowScope.ModeSegment(
    text: String,
    selected: Boolean,
    enabled: Boolean,
    onClick: () -> Unit,
) {
    val contentColor by animateColorAsState(
        targetValue = when {
            selected -> MaterialTheme.colorScheme.onPrimary
            enabled -> MaterialTheme.colorScheme.onSurfaceVariant
            else -> MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.38f)
        },
        label = "modeSegmentContent",
    )
    Surface(
        // Already-selected segments aren't clickable — there's nothing to switch to. The pill behind
        // the row paints the selection, so segments themselves stay transparent.
        onClick = onClick,
        enabled = enabled && !selected,
        shape = RoundedCornerShape(percent = 50),
        color = Color.Transparent,
        contentColor = contentColor,
        modifier = Modifier.weight(1f).semantics { this.selected = selected },
    ) {
        Box(
            modifier = Modifier.padding(vertical = 10.dp),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                text = text,
                style = MaterialTheme.typography.labelLarge,
                fontWeight = SemiBold,
            )
        }
    }
}

/**
 * The start screen's "everything else" drawer: app preferences (offline mode, system colors), dice
 * customization, the premium store, and the privacy link — all the secondary controls that used to
 * crowd the main screen, now one tap behind the gear. Opened from the gear affordance in
 * [BankoStartScreen]; mirrors the top-right Rules sheet.
 */
/** The pages reachable inside the settings sheet — a local drill-in stack, one sheet, no nesting. */
private enum class SettingsPage { Settings, Store, Dice }

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun BankoSettingsSheet(
    uiState: BankoStartScreenUiState,
    onDismiss: () -> Unit,
    startOnDice: Boolean = false,
) {
    val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    var page by remember(startOnDice) {
        mutableStateOf(if (startOnDice) SettingsPage.Dice else SettingsPage.Settings)
    }
    // The Store page is reachable from the settings row (full store) and from a locked dice tile
    // (Plus-only, for GOLD dice). Track the filter and where to return to on back, so we drill in
    // place instead of stacking a second sheet.
    var storePlusOnly by remember { mutableStateOf(false) }
    var storeReturnPage by remember { mutableStateOf(SettingsPage.Settings) }
    // The locked cosmetic whose tap opened the store, so the store can lead with a one-game
    // trial of exactly that item. Cleared when the store is reached any other way.
    var storeTrialSkin by remember { mutableStateOf<DiceSkin?>(null) }
    var storeTrialHat by remember { mutableStateOf<DiceHat?>(null) }
    val openStore = { plusOnly: Boolean, from: SettingsPage ->
        RemoveAdsPurchases.clearMessage()
        storePlusOnly = plusOnly
        storeReturnPage = from
        page = SettingsPage.Store
    }

    // Resolve the saved dice selection once, falling back to defaults when the saved choice is
    // currently locked. Shared by the settings preview row and the dice picker page.
    val rawSelectedDiceSkin by uiState.selectedDiceSkinFlow.collectAsState()
    val rawSelectedDiceHat by uiState.selectedDiceHatFlow.collectAsState()
    val purchaseState by RemoveAdsPurchases.uiState.collectAsState()
    val luckyUnlocked by Achievements.diceUnlocked.collectAsState()
    val creatorUnlocked by Achievements.creatorDiceUnlocked.collectAsState()
    val practiceUnlocked by Achievements.practiceDiceUnlocked.collectAsState()
    val earnedLimitedTimeSkinIds by LimitedTimeDiceEntitlements.earnedSkinIds.collectAsState()
    val activeLimitedTimeSettingIds = LimitedTimeGameModes.activeSettingIdsNow()
    val selectedDiceSkin = DiceSkin.fromId(rawSelectedDiceSkin)
        .takeIf {
            it.isUnlocked(
                purchaseState.adsRemoved,
                purchaseState.bankoPlusActive,
                luckyUnlocked,
                creatorUnlocked,
                practiceUnlocked,
                activeLimitedTimeSettingIds = activeLimitedTimeSettingIds,
                earnedLimitedTimeSkinIds = earnedLimitedTimeSkinIds,
            )
        }
        ?.id ?: DiceSkin.DEFAULT.id
    val selectedDiceHat = DiceHat.fromId(rawSelectedDiceHat)
        .takeIf { it.isUnlocked(purchaseState.adsRemoved, purchaseState.bankoPlusActive, luckyUnlocked, creatorUnlocked) }
        ?.id ?: DiceHat.NONE.id

    ModalBottomSheet(
        modifier = Modifier.statusBarsPadding(),
        onDismissRequest = {
            RemoveAdsPurchases.clearMessage()
            onDismiss()
        },
        sheetState = sheetState,
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
                .navigationBarsPadding()
                .padding(bottom = 24.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            // One header for all pages: a back arrow appears once we've drilled past Settings, so the
            // sub-pages (Store, Dice) live in the same sheet instead of stacking a second one.
            val canNavigateBack = when {
                page == SettingsPage.Settings -> false
                startOnDice && page == SettingsPage.Dice -> false
                else -> true
            }
            SheetHeader(
                title = when (page) {
                    SettingsPage.Settings -> stringResource(Res.string.settings)
                    SettingsPage.Store -> stringResource(
                        if (storePlusOnly) Res.string.store_plus_headline else Res.string.store_headline,
                    )
                    SettingsPage.Dice -> stringResource(Res.string.customize_dice)
                },
                onBack = if (!canNavigateBack) null else {
                    {
                        if (page == SettingsPage.Store) {
                            RemoveAdsPurchases.clearMessage()
                            page = storeReturnPage
                        } else {
                            page = SettingsPage.Settings
                        }
                    }
                },
            )
            when (page) {
                SettingsPage.Settings -> SettingsPageContent(
                    purchaseState = purchaseState,
                    onOpenStore = {
                        storeTrialSkin = null
                        storeTrialHat = null
                        openStore(false, SettingsPage.Settings)
                    },
                )
                SettingsPage.Store -> StorePageContent(
                    plusOnly = storePlusOnly,
                    trialSkin = storeTrialSkin,
                    trialHat = storeTrialHat,
                )
                SettingsPage.Dice -> DicePageContent(
                    selectedSkinId = selectedDiceSkin,
                    selectedHatId = selectedDiceHat,
                    onSkinSelected = uiState.onDiceSkinSelected,
                    onHatSelected = uiState.onDiceHatSelected,
                    onLockedCosmetic = { skin, hat ->
                        storeTrialSkin = skin
                        storeTrialHat = hat
                        val plusOnly = skin?.tier == DiceSkinTier.GOLD ||
                                skin?.tier == DiceSkinTier.LIMITED_PLUS ||
                                hat?.tier == DiceSkinTier.GOLD
                        openStore(plusOnly, SettingsPage.Dice)
                    },
                    // Launches the practice round from the Training Wheels unlock dialog; the
                    // resulting navigation tears this sheet down with the start screen.
                    onStartPractice = uiState.onQuickPlay,
                )
            }
        }
    }
}

@Composable
private fun selectedDiceIds(uiState: BankoStartScreenUiState): Pair<String, String> {
    val rawSelectedDiceSkin by uiState.selectedDiceSkinFlow.collectAsState()
    val rawSelectedDiceHat by uiState.selectedDiceHatFlow.collectAsState()
    val purchaseState by RemoveAdsPurchases.uiState.collectAsState()
    val luckyUnlocked by Achievements.diceUnlocked.collectAsState()
    val creatorUnlocked by Achievements.creatorDiceUnlocked.collectAsState()
    val practiceUnlocked by Achievements.practiceDiceUnlocked.collectAsState()
    val earnedLimitedTimeSkinIds by LimitedTimeDiceEntitlements.earnedSkinIds.collectAsState()
    val activeLimitedTimeSettingIds = LimitedTimeGameModes.activeSettingIdsNow()
    val selectedDiceSkin = DiceSkin.fromId(rawSelectedDiceSkin)
        .takeIf {
            it.isUnlocked(
                purchaseState.adsRemoved,
                purchaseState.bankoPlusActive,
                luckyUnlocked,
                creatorUnlocked,
                practiceUnlocked,
                activeLimitedTimeSettingIds = activeLimitedTimeSettingIds,
                earnedLimitedTimeSkinIds = earnedLimitedTimeSkinIds,
            )
        }
        ?.id ?: DiceSkin.DEFAULT.id
    val selectedDiceHat = DiceHat.fromId(rawSelectedDiceHat)
        .takeIf { it.isUnlocked(purchaseState.adsRemoved, purchaseState.bankoPlusActive, luckyUnlocked, creatorUnlocked) }
        ?.id ?: DiceHat.NONE.id
    return selectedDiceSkin to selectedDiceHat
}

/** A sheet's title row, with a leading back arrow when [onBack] is non-null (i.e. on a drilled page). */
@Composable
private fun SheetHeader(title: String, onBack: (() -> Unit)?) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        if (onBack != null) {
            Icon(
                imageVector = BankoIcons.ArrowBack,
                contentDescription = stringResource(Res.string.back),
                tint = MaterialTheme.colorScheme.onSurface,
                modifier = Modifier
                    .clip(RoundedCornerShape(50))
                    .clickable(onClick = onBack)
                    .padding(4.dp),
            )
        }
        Text(
            text = title,
            style = MaterialTheme.typography.titleLarge,
            fontWeight = SemiBold,
            color = MaterialTheme.colorScheme.onSurface,
        )
    }
}

/** The settings landing page: app preferences, the premium store link, and privacy. */
@Composable
private fun SettingsPageContent(
    purchaseState: RemoveAdsPurchaseUiState,
    onOpenStore: () -> Unit,
) {
    val uriHandler = LocalUriHandler.current

    Column(
        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        DiceDisplayModeToggle()
        SeasonalEffectsToggle()
        // Preferences card: Material You opt-in (Android 12+ only). Online vs single-device is now a
        // first-class chooser on the start screen, so this card only appears where it has content.
        if (theme.dynamicColorSupported()) {
            Surface(
                shape = RoundedCornerShape(16.dp),
                color = MaterialTheme.colorScheme.surfaceContainerHigh,
                shadowElevation = 2.dp,
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
                modifier = Modifier.fillMaxWidth(),
            ) {
                Column(modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp)) {
                    val playerPreferences = org.koin.compose.koinInject<PlayerPreferences>()
                    val dynamicColorOn by theme.ThemePrefs.dynamicColorEnabled.collectAsState()
                    SettingsToggleRow(
                        label = stringResource(Res.string.use_system_colors),
                        checked = dynamicColorOn,
                        onCheckedChange = { enabled ->
                            theme.ThemePrefs.dynamicColorEnabled.value = enabled
                            playerPreferences.setDynamicColorEnabled(enabled)
                        },
                    )
                }
            }
        }

        // Store — a single row that drills into the premium page, instead of a stack of buy buttons.
        if (purchaseState.isPurchasesSupported) {
            val storeSubtitle = when {
                purchaseState.bankoPlusActive -> stringResource(Res.string.banko_plus_active)
                // Removed ads but no Plus yet — nudge the subscription rather than dead-ending at "Ads removed".
                purchaseState.adsRemoved -> stringResource(Res.string.store_row_subtitle_plus)
                else -> stringResource(Res.string.store_row_subtitle)
            }
            SettingsLinkRow(
                title = stringResource(Res.string.store_row_title),
                subtitle = storeSubtitle,
                onClick = onOpenStore,
            )
        }

        TextButton(
            onClick = { uriHandler.openUri("https://bankothegame.com/privacy") },
            modifier = Modifier.align(Alignment.CenterHorizontally),
        ) {
            Text(text = stringResource(Res.string.privacy_policy))
        }
    }
}

/**
 * What to upsell given the user's purchase state, or null when there's nothing to sell (the user
 * already has Banko Plus, or the platform doesn't support purchases). Centralizes the rule every
 * store entry point shares so they all agree: one-time Remove-Ads buyers get steered to Plus, and
 * everyone else to the full ad-free store.
 */
internal data class StoreUpsell(val labelRes: StringResource, val plusOnly: Boolean)

internal fun storeUpsellFor(state: RemoveAdsPurchaseUiState): StoreUpsell? = when {
    !state.isPurchasesSupported || state.bankoPlusActive -> null
    // Action-oriented CTA labels (distinct from the store's title): an ads-removed user is sold Plus,
    // anyone still seeing ads is sold "Go ad-free".
    state.adsRemoved -> StoreUpsell(Res.string.store_plus_headline, plusOnly = true)
    else -> StoreUpsell(Res.string.cta_go_ad_free, plusOnly = false)
}

/**
 * Premium store as a standalone sheet — used from the in-game ad banner, where there's no settings
 * sheet to drill in from. From Settings, the same [StorePageContent] is shown in-place instead.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun BankoStoreSheet(onDismiss: () -> Unit, plusOnly: Boolean = false) {
    val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    // Start each visit clean — a leftover "Purchase did not complete." from a prior attempt
    // shouldn't sit statically at the bottom of the sheet.
    LaunchedEffect(Unit) { RemoveAdsPurchases.clearMessage() }
    ModalBottomSheet(
        modifier = Modifier.statusBarsPadding(),
        onDismissRequest = {
            RemoveAdsPurchases.clearMessage()
            onDismiss()
        },
        sheetState = sheetState,
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
                .navigationBarsPadding()
                .padding(bottom = 24.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            val headline =
                if (plusOnly) Res.string.store_plus_headline else Res.string.store_headline
            SheetHeader(title = stringResource(headline), onBack = null)
            StorePageContent(plusOnly = plusOnly)
        }
    }
}

/**
 * The store tiers (Remove Ads + Banko Plus) plus restore/status — shared by the sheet and the drill-in.
 * When [plusOnly] is true, the one-time Remove Ads tier is hidden so the sheet is a focused Banko Plus
 * upsell (e.g. opened from the Game Length "Plus" affordance).
 */
@Composable
private fun StorePageContent(
    plusOnly: Boolean = false,
    trialSkin: DiceSkin? = null,
    trialHat: DiceHat? = null,
) {
    val uriHandler = LocalUriHandler.current
    Column(
        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        val purchaseState by RemoveAdsPurchases.uiState.collectAsState()
        val actionsEnabled = purchaseState.isConfigured && !purchaseState.isLoading
        val hasVisibleStoreOptions =
            purchaseState.bankoPlusPriceLabel != null ||
                purchaseState.bankoPlusAnnualPriceLabel != null ||
                (!plusOnly && purchaseState.priceLabel != null)

        if (purchaseState.bankoPlusActive) {
            ActiveBankoPlusContent(
                plan = purchaseState.bankoPlusPlan,
                yearlyPrice = purchaseState.bankoPlusAnnualPriceLabel,
                managementUrl = purchaseState.subscriptionManagementUrl,
                actionsEnabled = actionsEnabled,
                onSwitchToYearly = RemoveAdsPurchases::purchaseBankoPlusAnnual,
                onManageSubscription = { url -> uriHandler.openUri(url) },
            )
        } else {
            // When a locked cosmetic opened this view, lead with a one-game ad trial of exactly
            // that item — the low-commitment answer to the question the tap asked — with the
            // purchase tiers below as the permanent option.
            if (BankoRewardedAd.isSupported && (trialSkin != null || trialHat != null)) {
                DiceTrialCard(trialSkin = trialSkin, trialHat = trialHat)
            }
            // On the Plus-focused view, lead with what Plus actually gives you so the value is
            // proactive (rather than only revealed when a player bumps into a lock).
            if (plusOnly) {
                PlusBenefitsSummary()
            }
            // Subscriptions first (cheapest entry), one-time last. Each card only renders once its
            // price has loaded from the current Offering.
            purchaseState.bankoPlusPriceLabel?.let { price ->
                TierCard(
                    title = stringResource(Res.string.tier_plus_monthly_title),
                    price = stringResource(Res.string.price_per_month, price),
                    benefits = listOf(
                        stringResource(Res.string.benefit_lobby_ad_free),
                        stringResource(Res.string.benefit_host_length),
                        stringResource(Res.string.benefit_plus_dice),
                    ),
                    enabled = actionsEnabled,
                    onClick = RemoveAdsPurchases::purchaseBankoPlus,
                )
            }
            purchaseState.bankoPlusAnnualPriceLabel?.let { price ->
                TierCard(
                    title = stringResource(Res.string.tier_plus_yearly_title),
                    price = stringResource(Res.string.price_per_year, price),
                    benefits = listOf(stringResource(Res.string.benefit_plus_everything)),
                    badge = stringResource(Res.string.tier_best_value),
                    enabled = actionsEnabled,
                    onClick = RemoveAdsPurchases::purchaseBankoPlusAnnual,
                )
            }
            if (!plusOnly) {
                purchaseState.priceLabel?.let { price ->
                    TierCard(
                        title = stringResource(Res.string.tier_remove_ads_title),
                        price = stringResource(Res.string.price_once, price),
                        benefits = listOf(
                            stringResource(Res.string.benefit_own_ads),
                            stringResource(Res.string.benefit_silver_dice),
                        ),
                        // The one-time tier is the only thing this purchase grants; mark it owned so
                        // we don't invite a second purchase, while still upselling Plus above.
                        owned = purchaseState.adsRemoved,
                        enabled = actionsEnabled,
                        onClick = RemoveAdsPurchases::purchaseRemoveAds,
                    )
                }
            }
            // Rewarded-ad Plus trial, below the paid tiers: independent of the purchase catalog,
            // hidden on platforms without ad support and for anyone who already has Plus. Kept out
            // of the plusOnly view (reached from a locked cosmetic or Plus setting) — the pass
            // doesn't unlock those, so offering it there would be a false affordance.
            if (!plusOnly && BankoRewardedAd.isSupported) {
                PlusPassCard()
            }
        }

        if (!purchaseState.bankoPlusActive && !hasVisibleStoreOptions) {
            Text(
                text = if (purchaseState.isLoading) {
                    stringResource(Res.string.store_loading_purchases)
                } else {
                    purchaseState.catalogMessage
                        ?: stringResource(Res.string.store_no_purchase_options)
                },
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.85f),
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth(),
            )
        }

        if (purchaseState.isLoading) {
            CircularProgressIndicator(modifier = Modifier.size(22.dp), strokeWidth = 2.dp)
        }

        if (!purchaseState.bankoPlusActive) {
            TextButton(
                onClick = RemoveAdsPurchases::restorePurchases,
                enabled = actionsEnabled,
            ) {
                Text(text = stringResource(Res.string.restore_purchases))
            }
        }

        purchaseState.catalogMessage
            ?.takeIf { !purchaseState.bankoPlusActive && hasVisibleStoreOptions }
            ?.let { message ->
                Text(
                    text = message,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.8f),
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth(),
                )
            }

        purchaseState.message?.let { message ->
            Text(
                text = message,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.8f),
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth(),
            )
        }

        Row(
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.fillMaxWidth(),
        ) {
            TextButton(onClick = { uriHandler.openUri("https://bankothegame.com/privacy") }) {
                Text(text = stringResource(Res.string.privacy_policy))
            }
            TextButton(
                onClick = {
                    uriHandler.openUri(
                        "https://www.apple.com/legal/internet-services/itunes/dev/stdeula/",
                    )
                },
            ) {
                Text(text = stringResource(Res.string.terms_of_use))
            }
        }
    }
}

@Composable
private fun ActiveBankoPlusContent(
    plan: BankoPlusPlan?,
    yearlyPrice: String?,
    managementUrl: String?,
    actionsEnabled: Boolean,
    onSwitchToYearly: () -> Unit,
    onManageSubscription: (String) -> Unit,
) {
    val planLabel = when (plan) {
        BankoPlusPlan.Monthly -> stringResource(Res.string.banko_plus_monthly_plan)
        BankoPlusPlan.Yearly -> stringResource(Res.string.banko_plus_yearly_plan)
        null -> stringResource(Res.string.banko_plus_plan_unknown)
    }
    val benefits = listOf(
        stringResource(Res.string.benefit_lobby_ad_free),
        stringResource(Res.string.benefit_host_length),
        stringResource(Res.string.benefit_plus_dice),
    )

    Surface(
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colorScheme.surfaceContainerHigh,
        shadowElevation = 2.dp,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.primary.copy(alpha = 0.55f)),
        modifier = Modifier.fillMaxWidth(),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 14.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(12.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Icon(
                    imageVector = BankoIcons.Star,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(28.dp),
                )
                Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
                    Text(
                        text = stringResource(Res.string.banko_plus_status_title),
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = SemiBold,
                        color = MaterialTheme.colorScheme.onSurface,
                    )
                    Text(
                        text = stringResource(Res.string.banko_plus_current_plan, planLabel),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.primary,
                    )
                }
            }

            Text(
                text = stringResource(Res.string.banko_plus_status_note),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )

            Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                benefits.forEach { benefit ->
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        Text(
                            text = "•",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                        Text(
                            text = benefit,
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                }
            }

            if (plan == BankoPlusPlan.Monthly && yearlyPrice != null) {
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                Text(
                    text = stringResource(Res.string.switch_to_yearly_hint),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.85f),
                )
                Button(
                    onClick = onSwitchToYearly,
                    enabled = actionsEnabled,
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Icon(
                        imageVector = BankoIcons.Star,
                        contentDescription = null,
                        modifier = Modifier.size(18.dp),
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(text = stringResource(Res.string.switch_to_yearly_with_price, yearlyPrice))
                }
            }

            managementUrl?.let { url ->
                OutlinedButton(
                    onClick = { onManageSubscription(url) },
                    enabled = actionsEnabled,
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Icon(
                        imageVector = BankoIcons.Settings,
                        contentDescription = null,
                        modifier = Modifier.size(18.dp),
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(text = stringResource(Res.string.manage_subscription))
                }
                Text(
                    text = stringResource(Res.string.subscription_manage_hint),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.85f),
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth(),
                )
            }

        }
    }
}

/**
 * A short, price-free summary of what Banko Plus unlocks, shown atop the Plus-focused store view so
 * the value proposition is stated up front before the priced tiers.
 */
@Composable
private fun PlusBenefitsSummary() {
    val benefits = listOf(
        stringResource(Res.string.benefit_lobby_ad_free),
        stringResource(Res.string.benefit_host_length),
        stringResource(Res.string.benefit_plus_dice),
    )
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        Text(
            text = stringResource(Res.string.plus_benefits_title),
            style = MaterialTheme.typography.titleSmall,
            fontWeight = SemiBold,
            color = MaterialTheme.colorScheme.onSurface,
        )
        benefits.forEach { benefit ->
            Text(
                text = "•  $benefit",
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

/**
 * One purchasable tier in the store sheet: a title (with an optional badge like "Best value"), the
 * price, and a bulleted benefit list. Tapping starts that tier's purchase unless it's already owned.
 */
/**
 * One-game trial of the locked cosmetic that opened the store: watch one ad and that exact
 * skin/hat rides on the player's dice for their next game (create or join), visible to the whole
 * lobby, then reverts. The card previews the item, names the one-game terms up front, and flips to
 * a "locked in" note once earned. Exactly one of [trialSkin]/[trialHat] is non-null.
 */
@Composable
private fun DiceTrialCard(trialSkin: DiceSkin?, trialHat: DiceHat?) {
    val pendingSkinId by DiceTrial.pendingSkinId.collectAsState()
    val pendingHatId by DiceTrial.pendingHatId.collectAsState()

    val displayName = trialSkin?.displayName ?: trialHat?.displayName ?: return
    val earned = (trialSkin != null && pendingSkinId == trialSkin.id) ||
            (trialHat != null && pendingHatId == trialHat.id)

    Surface(
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colorScheme.surfaceContainerHigh,
        shadowElevation = 2.dp,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        modifier = Modifier.fillMaxWidth(),
    ) {
        Column(
            modifier = Modifier.padding(horizontal = 16.dp, vertical = 14.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                // Hat previews get headroom for the flames rising off the die.
                Box(
                    modifier = Modifier
                        .width(48.dp)
                        .height(if (trialHat != null) 76.dp else 48.dp),
                    contentAlignment = Alignment.BottomCenter,
                ) {
                    DieFace(
                        number = 5,
                        skin = trialSkin?.style() ?: DiceSkin.DEFAULT.style(),
                        hat = trialHat ?: DiceHat.NONE,
                        size = 48.dp,
                        pipSize = 6.dp,
                        pipPadding = 8.dp,
                        cornerRadius = 10.dp,
                        fakeTimes = 0,
                        animated = false,
                    )
                }
                Spacer(modifier = Modifier.width(12.dp))
                Column {
                    Text(
                        text = stringResource(Res.string.dice_trial_title, displayName),
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = SemiBold,
                        color = MaterialTheme.colorScheme.onSurface,
                    )
                    Text(
                        text = stringResource(Res.string.dice_trial_body),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
            if (earned) {
                Text(
                    text = stringResource(Res.string.dice_trial_ready),
                    style = MaterialTheme.typography.labelLarge,
                    color = MaterialTheme.colorScheme.primary,
                )
            } else {
                WatchAdButton(onRewarded = {
                    trialSkin?.let {
                        DiceTrial.grantSkin(it.id)
                        Analytics.diceTrialEarned(it.id)
                    }
                    trialHat?.let {
                        DiceTrial.grantHat(it.id)
                        Analytics.diceTrialEarned(it.id)
                    }
                })
            }
        }
    }
}

/**
 * The rewarded-ad Plus trial: watch one ad, earn a [PlusPass] that gives the next created game
 * host perks (lobby-wide ad-free included). One pass at a time — while one is pending the button
 * gives way to a "ready" note until the pass is spent on a game.
 */
@Composable
private fun PlusPassCard() {
    val passPending by PlusPass.pending.collectAsState()

    Surface(
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colorScheme.surfaceContainerHigh,
        shadowElevation = 2.dp,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        modifier = Modifier.fillMaxWidth(),
    ) {
        Column(
            modifier = Modifier.padding(horizontal = 16.dp, vertical = 14.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            Text(
                text = stringResource(Res.string.plus_pass_title),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = SemiBold,
                color = MaterialTheme.colorScheme.onSurface,
            )
            Text(
                text = stringResource(Res.string.plus_pass_body),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            if (passPending) {
                Text(
                    text = stringResource(Res.string.plus_pass_ready),
                    style = MaterialTheme.typography.labelLarge,
                    color = MaterialTheme.colorScheme.primary,
                )
            } else {
                WatchAdButton(onRewarded = {
                    PlusPass.grant()
                    Analytics.plusPassEarned()
                })
            }
        }
    }
}

@Composable
private fun TierCard(
    title: String,
    price: String,
    benefits: List<String>,
    badge: String? = null,
    owned: Boolean = false,
    enabled: Boolean,
    onClick: () -> Unit,
) {
    Surface(
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colorScheme.surfaceContainerHigh,
        shadowElevation = 2.dp,
        border = BorderStroke(
            1.dp,
            if (badge != null) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.outlineVariant,
        ),
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .clickable(enabled = enabled && !owned, onClick = onClick),
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .alpha(if (owned) 0.6f else 1f)
                .padding(horizontal = 16.dp, vertical = 14.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = title,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = SemiBold,
                    color = MaterialTheme.colorScheme.onSurface,
                )
                val chip = when {
                    owned -> stringResource(Res.string.tier_owned)
                    else -> badge
                }
                if (chip != null) {
                    Surface(
                        shape = RoundedCornerShape(8.dp),
                        color = MaterialTheme.colorScheme.primary,
                    ) {
                        Text(
                            text = chip,
                            style = MaterialTheme.typography.labelSmall,
                            fontWeight = SemiBold,
                            color = MaterialTheme.colorScheme.onPrimary,
                            modifier = Modifier.padding(horizontal = 8.dp, vertical = 2.dp),
                        )
                    }
                }
            }
            Text(
                text = price,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = SemiBold,
                color = MaterialTheme.colorScheme.primary,
            )
            benefits.forEach { benefit ->
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text(
                        text = "•",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                    Text(
                        text = benefit,
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
        }
    }
}

/** A label + Switch row, used for the boolean preferences in [BankoSettingsSheet]. */
@Composable
private fun SettingsToggleRow(
    label: String,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.bodyLarge,
            color = MaterialTheme.colorScheme.onSurface,
        )
        Switch(checked = checked, onCheckedChange = onCheckedChange)
    }
}

/** A tappable card row (title + subtitle + chevron) that drills into another sheet/screen. */
@Composable
private fun SettingsLinkRow(title: String, subtitle: String, onClick: () -> Unit) {
    Surface(
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colorScheme.surfaceContainerHigh,
        shadowElevation = 2.dp,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .clickable(onClick = onClick),
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 16.dp, vertical = 14.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = title,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = SemiBold,
                    color = MaterialTheme.colorScheme.onSurface,
                )
                Text(
                    text = subtitle,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.85f),
                )
            }
            Icon(
                imageVector = BankoIcons.ChevronRight,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary,
            )
        }
    }
}

/** The player's currently-selected dice (base skin + effect), previewed, with a "Change" affordance. */
@Composable
private fun CurrentDiceEntry(
    selectedSkinId: String,
    selectedHatId: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier.fillMaxWidth(),
) {
    val skin = DiceSkin.fromId(selectedSkinId)
    val hat = DiceHat.fromId(selectedHatId)
    val subtitle = if (hat == DiceHat.NONE) skin.displayName else "${skin.displayName} · ${hat.displayName}"
    Surface(
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colorScheme.surfaceContainerHigh,
        shadowElevation = 2.dp,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        modifier = Modifier
            .then(modifier)
            .clip(RoundedCornerShape(16.dp))
            .clickable(onClick = onClick),
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            DieFace(
                number = 5,
                skin = skin.style(),
                hat = hat,
                size = 44.dp,
                pipSize = 6.dp,
                pipPadding = 8.dp,
                cornerRadius = 10.dp,
                fakeTimes = 0,
                animated = false,
            )
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = stringResource(Res.string.your_dice),
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = SemiBold,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Text(
                    text = subtitle,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.75f),
                )
            }
            Text(
                text = stringResource(Res.string.change),
                style = MaterialTheme.typography.labelLarge,
                color = MaterialTheme.colorScheme.primary,
            )
        }
    }
}

/**
 * Two-layer dice customizer page (base-skin row + effect row), shown in-place inside the settings
 * sheet. Both selections are saved to [PlayerPreferences] and written on the synced Player doc, so the
 * chosen combo renders for everyone when this player rolls. Locked items show a lock; tapping a paid
 * one starts its purchase.
 */
@Composable
private fun DicePageContent(
    selectedSkinId: String,
    selectedHatId: String,
    onSkinSelected: (String) -> Unit,
    onHatSelected: (String) -> Unit,
    onLockedCosmetic: (DiceSkin?, DiceHat?) -> Unit,
    onStartPractice: () -> Unit,
) {
    val purchaseState by RemoveAdsPurchases.uiState.collectAsState()
    val luckyUnlocked by Achievements.diceUnlocked.collectAsState()
    val creatorUnlocked by Achievements.creatorDiceUnlocked.collectAsState()
    val practiceUnlocked by Achievements.practiceDiceUnlocked.collectAsState()
    val earnedLimitedTimeSkinIds by LimitedTimeDiceEntitlements.earnedSkinIds.collectAsState()
    val activeLimitedTimeSettingIds = LimitedTimeGameModes.activeSettingIdsNow()
    // Tapping a locked tile leads somewhere useful: paid tiers drill into the store page carrying
    // the tapped cosmetic (so the store can lead with a one-game ad trial of exactly that item),
    // while the practice-earned tier explains its unlock and offers to launch the practice round.
    var showPracticeUnlockDialog by remember { mutableStateOf(false) }

    if (showPracticeUnlockDialog) {
        AlertDialog(
            onDismissRequest = { showPracticeUnlockDialog = false },
            title = { Text(text = stringResource(Res.string.practice_dice_dialog_title)) },
            text = { Text(text = stringResource(Res.string.practice_dice_dialog_body)) },
            confirmButton = {
                Button(
                    onClick = {
                        showPracticeUnlockDialog = false
                        onStartPractice()
                    },
                    shape = RoundedCornerShape(16.dp),
                ) {
                    Text(text = stringResource(Res.string.practice_offer_play))
                }
            },
            dismissButton = {
                TextButton(onClick = { showPracticeUnlockDialog = false }) {
                    Text(text = stringResource(Res.string.practice_dice_dialog_dismiss))
                }
            },
        )
    }

    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Box(Modifier.padding(horizontal = 16.dp)) { DiceDisplayModeToggle() }
        // Layer 1: base skin (the material).
        CosmeticRowHeader(stringResource(Res.string.dice_section_base))
        LazyRow(
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            contentPadding = PaddingValues(horizontal = 16.dp),
        ) {
            items(
                visibleDiceSkinsForPicker(
                    adsRemoved = purchaseState.adsRemoved,
                    bankoPlusActive = purchaseState.bankoPlusActive,
                    luckyUnlocked = luckyUnlocked,
                    creatorUnlocked = creatorUnlocked,
                    practiceUnlocked = practiceUnlocked,
                    activeLimitedTimeSettingIds = activeLimitedTimeSettingIds,
                    earnedLimitedTimeSkinIds = earnedLimitedTimeSkinIds,
                )
            ) { skin ->
                CosmeticOption(
                    previewSkin = skin.style(),
                    previewHat = DiceHat.NONE,
                    label = skin.displayName,
                    tier = skin.tier,
                    selected = skin.id == selectedSkinId,
                    unlocked = skin.isUnlocked(
                        purchaseState.adsRemoved,
                        purchaseState.bankoPlusActive,
                        luckyUnlocked,
                        creatorUnlocked,
                        practiceUnlocked,
                        activeLimitedTimeSettingIds = activeLimitedTimeSettingIds,
                        earnedLimitedTimeSkinIds = earnedLimitedTimeSkinIds,
                    ),
                    topRoom = 0.dp,
                    animated = false,
                    onSelect = { onSkinSelected(skin.id) },
                    onPurchase = {
                        if (skin.tier == DiceSkinTier.PRACTICE) {
                            showPracticeUnlockDialog = true
                        } else {
                            onLockedCosmetic(skin, null)
                        }
                    },
                    purchasesSupported = purchaseState.isPurchasesSupported,
                )
            }
        }

        // Layer 2: hat (worn on top). Previewed on the currently-selected base skin. The tiles get
        // extra top room so the flames that rise above the die show fully instead of being clipped.
        CosmeticRowHeader(stringResource(Res.string.dice_section_hats))
        LazyRow(
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            contentPadding = PaddingValues(horizontal = 16.dp),
        ) {
            items(DiceHat.entries) { hat ->
                CosmeticOption(
                    // Preview hats on a plain die so the flame reads clearly and we don't stack a
                    // second live fire (an Ember base) under each tile.
                    previewSkin = DiceSkin.DEFAULT.style(),
                    previewHat = hat,
                    label = hat.displayName,
                    tier = hat.tier,
                    selected = hat.id == selectedHatId,
                    unlocked = hat.isUnlocked(
                        purchaseState.adsRemoved,
                        purchaseState.bankoPlusActive,
                        luckyUnlocked,
                        creatorUnlocked,
                    ),
                    topRoom = 48.dp,
                    // Only the two flame-hat tiles animate (the showcase) — few enough to stay smooth.
                    animated = true,
                    onSelect = { onHatSelected(hat.id) },
                    onPurchase = { onLockedCosmetic(null, hat) },
                    purchasesSupported = purchaseState.isPurchasesSupported,
                )
            }
        }
    }
}

@Composable
private fun CosmeticRowHeader(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelLarge,
        fontWeight = SemiBold,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        modifier = Modifier.padding(horizontal = 16.dp, vertical = 4.dp),
    )
}

/**
 * One selectable cosmetic tile (used for both base skins and effects): a die preview, a label, and —
 * when locked — a lock + how-to-unlock hint. Unlocked → selects it; locked paid → opens the store
 * sheet (via [onPurchase]) so the player sees prices and picks a plan, rather than firing an instant
 * native purchase.
 */
@Composable
private fun CosmeticOption(
    previewSkin: DiceSkinStyle,
    previewHat: DiceHat,
    label: String,
    tier: DiceSkinTier,
    selected: Boolean,
    unlocked: Boolean,
    topRoom: Dp,
    animated: Boolean,
    onSelect: () -> Unit,
    onPurchase: () -> Unit = {},
    purchasesSupported: Boolean = true,
) {
    // Without purchase support (e.g. web) a locked paid tile has nowhere to lead — leave it shown as
    // locked but non-tappable rather than drilling into an empty store. The practice-earned tier is
    // always tappable while locked: its tap opens the how-to-unlock dialog, not the store.
    val purchasable = !unlocked && purchasesSupported &&
            (tier == DiceSkinTier.SILVER ||
                tier == DiceSkinTier.GOLD ||
                tier == DiceSkinTier.LIMITED_PLUS)
    val lockedTapTarget = purchasable || (!unlocked && tier == DiceSkinTier.PRACTICE)
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(2.dp),
        modifier = Modifier
            .clip(RoundedCornerShape(12.dp))
            .clickable(enabled = unlocked || lockedTapTarget) {
                if (unlocked) onSelect() else onPurchase()
            }
            .padding(4.dp),
    ) {
        // Cell is taller than the die by [topRoom] with the die at the bottom, so a hat's flames
        // rise within the cell instead of overflowing into the (clipped) row.
        Box(
            modifier = Modifier.width(60.dp).height(60.dp + topRoom),
            contentAlignment = Alignment.BottomCenter,
        ) {
            Box(contentAlignment = Alignment.Center) {
                DieFace(
                    number = 5,
                    skin = previewSkin,
                    hat = previewHat,
                    size = 60.dp,
                    modifier = Modifier
                        .border(
                            width = if (selected) 3.dp else 1.dp,
                            color = if (selected) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.outlineVariant,
                            shape = RoundedCornerShape(12.dp),
                        )
                        .alpha(if (unlocked) 1f else 0.35f),
                    pipSize = 8.dp,
                    pipPadding = 10.dp,
                    cornerRadius = 12.dp,
                    fakeTimes = 0,
                    animated = animated,
                )
                if (!unlocked) {
                    Icon(
                        imageVector = BankoIcons.Lock,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onSurface,
                        modifier = Modifier.size(22.dp),
                    )
                }
            }
        }
        Text(
            text = label,
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onBackground,
        )
        if (!unlocked) {
            val unlockHint = when (tier) {
                DiceSkinTier.SILVER -> stringResource(Res.string.dice_unlock_silver)
                DiceSkinTier.GOLD -> stringResource(Res.string.dice_unlock_gold)
                DiceSkinTier.LUCKY -> stringResource(Res.string.dice_unlock_lucky)
                DiceSkinTier.CREATOR -> stringResource(Res.string.dice_unlock_creator)
                DiceSkinTier.PRACTICE -> stringResource(Res.string.dice_unlock_practice)
                DiceSkinTier.LIMITED_PLUS -> stringResource(Res.string.dice_unlock_limited_plus)
                DiceSkinTier.FREE -> ""
            }
            Text(
                text = unlockHint,
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.6f),
            )
        }
    }
}
